/**
 * 표시 계층 — enum 원값을 화면 표기로 바꾸는 유일한 자리.
 *
 * 🔴 규약: 코드·상태·API 는 계속 원값(`batch` · `running` · `orchestrator`)을 쓴다.
 *    한글로 바꾸는 일은 **여기서만** 한다. 도메인 타입에 한글을 넣지 않는다.
 *
 * 스키마 필드명·enum 영문이 화면에 나오면 안 된다(와이어프레임 README §1).
 * 그 규율을 지키는 방법은 **사전을 전수로 채우는 것**이지 모르는 값을 지우는 것이 아니다.
 *
 * 🔴 **모르는 값은 «원값»으로 떨어진다 — `—` 도 빈 문자열도 아니다**(eric 확정 2026-09-20).
 *    종전에는 `—` 였고 그래서 실물 배치에서 **배지 안이 비어** 나왔다(`WAITING_FOR_UNLOAD`).
 *    빈 배지·`—` 배지는 「상태 없음」으로 읽히는데, 그때 장비는 실제로 돌고 있었다.
 *    **사전에 없다는 사실 자체가 정보다** — 로그 화면의 에러 코드가 이미 같은 판단을 했다
 *    (`LogsScreen.eventContentText`). 원값이 보이면 사전에 채울 수 있고, `—` 는 아무도 못 본다.
 */
import { hasMessage, t, type MessageKey } from './t.ts'

/** `enum.batchStatus.running` 같은 키에서 마지막 세그먼트만 뽑아 값 유니온을 만든다. */
type Suffix<K, P extends string> = K extends `${P}.${infer S}` ? S : never

export type BatchStatus = Suffix<MessageKey, 'enum.batchStatus'>
/** 실행 생애 축(`ExecutionStream.type`). 🔴 배치 상태와 **다른 축**이다 — 주어가 실행이다 */
export type ExecutionEvent = Suffix<MessageKey, 'enum.executionEvent'>
export type ModuleState = Suffix<MessageKey, 'enum.moduleState'>
export type RecipeStatus = Suffix<MessageKey, 'enum.recipeStatus'>
export type SampleStatus = Suffix<MessageKey, 'enum.sampleStatus'>
export type ControlOwner = Suffix<MessageKey, 'enum.controlOwner'>
export type LayoutDetail = Suffix<MessageKey, 'enum.layoutDetail'>
export type LabwareType = Suffix<MessageKey, 'enum.labwareType'>
export type Verdict = Suffix<MessageKey, 'enum.verdict'>
export type Severity = Suffix<MessageKey, 'enum.severity'>
/**
 * 알람 분류(`Alarm.category`). 🔴 **되살렸다**(2026-09-26) — 2026-09-20 에 「계약·실물·DB 열 어디에도
 * 없다」로 걷었던 것인데 그 서술이 틀리게 됐다. 근거: `ctcells-core db/migrations/0014_alarm_active_category.sql`
 * (열 신설) · `app/services/alarms.py:620`(적재 시점 `catalog_category()`) · `app/views/alarms.py:97`(`alarm_dto`).
 * ⚠ 계약은 `string` 이다(어휘를 열거하지 않는다) — 카탈로그가 값을 늘리면 사전에 없는 값이 오고,
 *   그때는 `enumLabel()` 규약대로 **원값**이 보인다.
 */
export type AlarmCategory = Suffix<MessageKey, 'enum.alarmCategory'>
export type SafetyState = Suffix<MessageKey, 'enum.safetyState'>
export type ReviewReason = Suffix<MessageKey, 'enum.reviewReason'>
export type ModuleId = Suffix<MessageKey, 'module'>
export type RoleName = 'admin' | 'service' | 'operator' | 'viewer'

/**
 * 서버에서 온 값처럼 타입이 보장되지 않는 문자열을 라벨로 바꾼다.
 *
 * - 값이 **없으면**(`null`·`''`) `—` — 「서버가 안 줬다」는 사실이다
 * - 값이 **있는데 사전에 없으면** 그 **원값** — 「모르는 값을 받았다」는 사실이다
 *
 * 둘은 다른 사실이라 같은 기호로 뭉개지 않는다.
 */
export function enumLabel(prefix: string, value: string | null | undefined): string {
  if (value == null || value === '') return t('mask.none')
  const key = `${prefix}.${value}`
  if (!hasMessage(key)) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] 사전에 없는 enum 값: ${key}`)
    }
    return value
  }
  return t(key)
}

/*
 * 🔴 **아래 `*Label()` 은 전부 `enumLabel()` 을 거친다 — `t()` 로 직행하지 않는다.**
 *    서명은 유니온이지만 **실제 인자는 서버 문자열을 캐스팅한 것**이다(`RobotRail`·
 *    `MonitoringScreen` 이 `snapshot.state as ModuleState` 로 넘긴다). `t()` 는 사전에 없는
 *    키를 받으면 **`undefined` 를 `string` 이라고 말하고 돌려줬고**, React 가 그것을 아무것도
 *    안 그려서 **배지가 비었다**(실물 `WAITING_FOR_UNLOAD` · 2026-09-20). 타입이 막아 줄 것
 *    같지만 캐스트 한 줄이면 뚫린다 — 그래서 런타임 방어를 여기 둔다.
 */
export const batchStatusLabel = (v: BatchStatus): string => enumLabel('enum.batchStatus', v)
export const executionEventLabel = (v: ExecutionEvent): string =>
  enumLabel('enum.executionEvent', v)
export const moduleStateLabel = (v: ModuleState): string => enumLabel('enum.moduleState', v)
export const recipeStatusLabel = (v: RecipeStatus): string => enumLabel('enum.recipeStatus', v)
export const sampleStatusLabel = (v: SampleStatus): string => enumLabel('enum.sampleStatus', v)
export const controlOwnerLabel = (v: ControlOwner): string => enumLabel('enum.controlOwner', v)
export const layoutDetailLabel = (v: LayoutDetail): string => enumLabel('enum.layoutDetail', v)
export const labwareTypeLabel = (v: LabwareType): string => enumLabel('enum.labwareType', v)
export const verdictLabel = (v: Verdict): string => enumLabel('enum.verdict', v)
export const severityLabel = (v: Severity): string => enumLabel('enum.severity', v)
/** 🔴 인자가 `string` 이다 — 서버 값이 카탈로그 판에 따라 늘어난다. 부재(`undefined`·`''`)는 `—` */
export const alarmCategoryLabel = (v: string | null | undefined): string =>
  enumLabel('enum.alarmCategory', v)
export const safetyStateLabel = (v: SafetyState): string => enumLabel('enum.safetyState', v)
export const reviewReasonLabel = (v: ReviewReason): string => enumLabel('enum.reviewReason', v)
/**
 * **프론트 모듈 키** 전용 — `cent` · `asm` · `stain` · `robot` · `vision` · `safety` · `system`.
 *
 * 🔴 **서버가 준 `module_id` 를 `as ModuleId` 로 밀어 넣지 마라**(2026-09-21 실측 버그).
 *    실물 ID 는 `centrifuge-1` · `assembler-1` · `robot6-1` 꼴이라 이 사전에 **없다** —
 *    캐스트가 타입을 뚫고 들어와 모듈 상세 제목이 **`centrifuge-1` 로 떴다.** ID 꼴은 계약이
 *    정한 게 아니라 **등록할 때 사람이 붙인 이름**이라, 앞자리를 잘라 종류를 읽어내는 것도
 *    답이 아니다. 종류는 서버가 따로 주는 `module_type` 에서 읽는다 → :func:`moduleTypeLabel`.
 * ⚠ 여기 쓰는 것이 맞는 자리: 라우트·탭·목 fixture 처럼 **프론트가 고른 키**를 보이는 곳.
 */
export const moduleLabel = (v: ModuleId): string => enumLabel('module', v)

/**
 * `ModuleSnapshot.module_type` 원값 → 사전 키. 서버 어휘와 화면 키가 다른 유일한 자리라
 * 표를 하나만 둔다. 출처는 계약(`orchestrator_api.openapi.yaml` `ModuleSnapshot.module_type`).
 *
 * ⚠ `ceiling_3axis`(천정형 직교)는 **없다.** 계약에는 있으나 eric 지시로 그 화면을 만들지
 *   않아 한글 표기가 없다 — 지어내지 않고 「모른다」로 둔다.
 */
const MODULE_TYPE_KEYS: Readonly<Record<string, ModuleId>> = {
  centrifuge: 'cent',
  assembler: 'asm',
  staining: 'stain',
  collaborative_6axis: 'robot',
}

/**
 * 모듈 종류 → 한글 표기. **모르는 종류는 `undefined`** 다.
 *
 * 🔴 `—` 가 아니라 `undefined` 인 이유: 부르는 쪽마다 처분이 다르다. 표의 한 칸이면 `—` 를
 *    찍어야 하고(칸이 비면 열이 어긋난다), 문장의 한 조각이면 **그 조각만 빼야** 한다
 *    (「— · 모듈 내 스텝 3/9」는 말이 안 된다). 그 판단을 부르는 쪽에 남긴다.
 * ⚠ `enumLabel()` 과 달리 원값으로 떨어지지 않는다 — 여기 오는 값은 enum 이 아니라
 *   **모듈 등록 종류**라, 모르는 종류를 화면에 흘리면 `collaborative_6axis` 같은 것이 뜬다.
 */
export function moduleTypeLabel(moduleType: string | null | undefined): string | undefined {
  const key = moduleTypeKey(moduleType)
  return key === undefined ? undefined : moduleLabel(key)
}

/**
 * 모듈 종류(`module_type` 원값) → 프론트 모듈 키. 표는 위 `MODULE_TYPE_KEYS` 하나다 —
 * 작업 줄의 구간 진행바가 「지금 어느 구간인가」를 이 키로 맞춘다. 모르는 종류는 `undefined`.
 */
export function moduleTypeKey(moduleType: string | null | undefined): ModuleId | undefined {
  if (moduleType == null) return undefined
  return MODULE_TYPE_KEYS[moduleType]
}

/**
 * 환자 식별자 · 시료 ID 마스킹. `view` 권한이 없는 역할(service)에게 가린다.
 * 집행은 서버다 — 여기는 노출만 가른다.
 */
export function maskPii(value: string | null | undefined, canView: boolean): string {
  if (!canView) return t('mask.pii')
  return value == null || value === '' ? t('mask.none') : value
}
